import React, { useState, useEffect, forwardRef } from 'react';
import {
  datatypeService,
  datasetService,
  entityService,
  fieldService,
  attributeService,
  attributeRoleService,
  attributeSensitivityService,
  attributeUnitService,
  glossaryService,
} from '../services';
import {
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Select,
  SelectButtons,
  SelectOwner,
  InputStatus,
  formatSelectOptions,
  Icon,
  Input,
  Textarea,
  TextareaAutosize,
  TextareaCode,
  textareaCodeInitialState,
  TextareaReadOnly,
  textareaToPlainText,
  textareaInitialState,
  textareaToRaw,
  editorStateToPlainText,
  PrincipalLink,
  Pill,
  Label,
} from '../components';
import {
  AttributeRole,
  AttributeSensitivity,
  fetchAssetsWithSameName,
  formatUserFriendly,
  formatPath,
  formatDatasetSegment,
  getBatchEditValue,
  Asset,
  technicalDefinitionLanguage,
  getHierarchyPath,
  getSameNameValidation,
} from '../helpers';
import {
  useCustomProperties,
  useDebounce,
  useDescriptionSource,
  useGlobalState,
  useTooltip,
  useSearch,
  useMessage,
} from '../hooks';

const _AddFieldForm = ({ mode, data, submitForm, loading, setLoading, formErrors = {} }, ref) => {
  const initialFormState = {
    field_id: null,
    field_name: '',
    field_technical_definition: textareaCodeInitialState({}),
    datatype_id: null,
    datatype_precision: '',
    datatype_scale: '',
    datatype_length: '',
    is_nullable: true,
    default_value: '',
    dataset_id: null,
    parent_field_id: null,
    is_primary_key: false,
    attribute_id: null,
    field_source_description: textareaInitialState({}),
    field_source_description_secondary: textareaInitialState({}),
    field_description_source: 'k',
    status_id: '',
  };

  const initialAttribute = {
    attribute_action: 'update',
    attribute_id: null,
    attribute_name: '',
    attribute_description: textareaInitialState({}),
    attribute_definition: textareaInitialState({}),
    is_pii: false,
    attribute_role_id: null,
    attribute_sensitivity_id: '',
    attribute_unit_id: null,
    glossary_id: null,
    parent_entity_id: null,
    status_id: '',
    owner_principal_id: '',
  };

  const initialBatchEditItems = {
    batch_datatype_id: false,
    batch_datatype_precision: false,
    batch_datatype_scale: false,
    batch_datatype_length: false,
    batch_is_nullable: false,
    batch_default_value: false,
    batch_dataset_id: false,
    batch_parent_field_id: false,
    batch_attribute_id: false,
    batch_field_description_source: false,
    batch_status_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentDataset] = useGlobalState('currentDataset');
  const [currentDatasetGroup] = useGlobalState('currentDatasetGroup');
  const [statuses] = useGlobalState('statuses');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [tmpFieldAssociations, setTmpFieldAssociations] = useState([]);
  const [datatypes, setDatatypes] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [fields, setFields] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attribute, setAttribute] = useState(initialAttribute);
  const [attributeAssocFields, setAttributeAssocFields] = useState([]);
  const [assetsWithSameName, setAssetsWithSameName] = useState({ attributes: [], entities: [] });
  const [newAttribute, setNewAttribute] = useState(initialAttribute);
  const [addAttribute, setAddAttribute] = useState(false);
  const [attributeRoles, setAttributeRoles] = useState([]);
  const [glossaries, setGlossaries] = useState([]);
  const [parentEntities, setParentEntities] = useState([]);
  const [attributeSensitivities, setAttributeSensitivities] = useState([]);
  const [attributeUnits, setAttributeUnits] = useState([]);

  // A unit only applies to a Measure, since that is the role whose values are quantified. Resolved
  // from the role's name rather than its id, which is a surrogate key.
  const newAttributeIsMeasure =
    attributeRoles
      .find(role => role.attribute_role_id === newAttribute.attribute_role_id)
      ?.attribute_role_name?.toLowerCase() === AttributeRole.measure.toLowerCase();

  const [loadingMoreOptions, setLoadingMoreOptions] = useState(false);
  const [loadingParentFields, setLoadingParentFields] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({
    tab: ['add', 'add_child'].includes(mode) ? 'general' : 'description',
  });
  const [requestResults, setRequestResults] = useState([]);
  const [fDRequestResults, setFDRequestResults] = useState([]);

  const { showMessage, hideMessage } = useMessage();
  const { showTooltip, hideTooltip } = useTooltip();
  const { getDescription, getDescriptionSourceOptions } = useDescriptionSource({
    data,
    formData,
    objectName: 'field',
  });
  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchAllSearchResults,
    clearSearchResults,
    loadNextPage,
  } = useSearch({
    setLoading: setLoadingMoreOptions,
    objectName: 'field',
    pagination: true,
    enableHistory: false,
  });
  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.Field,
    // In add_child, data[0] is the PARENT field, so its custom property values must not be loaded —
    // the child would open pre-filled with values belonging to its parent. Every other mode does read
    // them: editing shows the field's own values, and adding with a field selected copies it.
    objectIds: mode !== 'add_child' ? data.map(x => x.field_id) : [],
  });

  const fd = useSearch({
    setLoading: setLoadingMoreOptions,
    objectName: 'attribute',
    pagination: true,
    enableHistory: false,
  });

  // Which group's datasets the dataset picker offers: the field's own group when editing, otherwise
  // the group being viewed. Read from currentDatasetGroup rather than off the current dataset, since
  // a dataset group's own page has no current dataset — every view that can open this form publishes
  // the group, only some publish a dataset.
  let dataset_group_id = data[0]?.dataset_group_id || currentDatasetGroup?.dataset_group_id;

  useEffect(() => {
    // Get form data
    fetchDatatypes();
    fetchDatasets();
    fetchAttributes();
    fetchAttributeRoles();
    fetchAttributeSensitivities();
    fetchAttributeUnits();
    fetchGlossaries();

    fetchParentFields(data[0]?.dataset_id || currentDataset?.dataset_id);
  }, []);

  useEffect(() => {
    setRequestResults(
      searchResults.reduce((res, result) => {
        const isSelf = data.length > 0 && result.searchable.field_id === data[0].field_id;

        const selectedByOther =
          result.searchable.attribute_id &&
          result.searchable.attribute_id.toString() !== formData.attribute_id
            ? true
            : false;
        res.push({
          value: result.searchable.field_id,
          label: result.searchable.field_name,
          icon: result.searchable.datatype_category,
          secondaryLabel: formatPath([
            result.searchable.system_name,
            result.searchable.datasource_name,
            result.searchable.dataset_group_name,
            result.searchable.dataset_name,
          ]),
          selectedByOther: selectedByOther,
          tooltip: isSelf
            ? 'This is the field you are currently editing'
            : selectedByOther
              ? `This Field is already associated with Attribute:\n${result.searchable.attribute_name}`
              : '',
          disabled: isSelf,
        });
        return res;
      }, [])
    );
  }, [searchResults]);

  useEffect(() => {
    let result = fd.searchResults.reduce((res, result) => {
      res.push({
        value: result.searchable.attribute_id,
        label: result.searchable.attribute_name,
        icon: result.searchable.attribute_role_name,
        tooltip: result.searchable.attribute_description,
      });
      return res;
    }, []);

    setFDRequestResults(result);
  }, [fd.searchResults]);

  useEffect(() => {
    setBatchEdit(false);
    setAddAttribute(false);

    // A single row is the field to copy — "add another like this" — but only if it is a field. A row
    // carrying no field_id is context rather than a field: adding to a selected dataset passes the
    // dataset that way, and reading it as a field to copy blanked the form's own defaults and left
    // attribute_id undefined, which the associated-attribute section below then tried to render.
    if (data.length === 1 && mode !== 'add_child' && data[0].field_id) {
      setFormData(prev => {
        return {
          ...prev,
          field_id: data[0].field_id,
          field_name: data[0].field_name || '',
          field_technical_definition: textareaCodeInitialState({
            value: data[0].field_technical_definition || '',
          }),
          datatype_id: data[0].datatype_id,
          dataset_id: data[0].dataset_id,
          parent_field_id: data[0].parent_field_id,
          datatype_length: data[0].datatype_length,
          datatype_precision: data[0].datatype_precision,
          datatype_scale: data[0].datatype_scale,
          is_nullable: data[0].is_nullable,
          default_value: data[0].default_value,
          is_primary_key: data[0].is_primary_key,
          attribute_id: data[0].attribute_id,
          field_source_description: textareaInitialState({
            value: data[0].field_source_description || '',
          }),
          field_source_description_secondary: textareaInitialState({
            value: data[0].field_source_description_secondary || '',
          }),
          field_description_source:
            data[0].field_description_source || prev.field_description_source,
          status_id: data[0].status_id,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        datatype_id: getBatchEditValue(data, 'datatype_id'),
        dataset_id: getBatchEditValue(data, 'dataset_id'),
        parent_field_id: getBatchEditValue(data, 'parent_field_id'),
        attribute_id: getBatchEditValue(data, 'attribute_id'),
        datatype_length: getBatchEditValue(data, 'datatype_length'),
        datatype_precision: getBatchEditValue(data, 'datatype_precision'),
        datatype_scale: getBatchEditValue(data, 'datatype_scale'),
        default_value: getBatchEditValue(data, 'default_value'),
        is_nullable: getBatchEditValue(data, 'is_nullable'),
        field_description_source: getBatchEditValue(data, 'field_description_source'),
        status_id: getBatchEditValue(data, 'status_id'),
      });
    } else if (mode === 'add_child') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        // The dataset comes from the field being added under. The dataset currently being viewed is
        // only the fallback: a field can be added from a list that is not a single dataset's own.
        dataset_id: data[0].dataset_id || currentDataset?.dataset_id,
        parent_field_id: data[0].field_id,
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        // The dataset the new field belongs to: the row it is being added under when there is one,
        // otherwise the dataset being viewed.
        dataset_id: data[0]?.dataset_id || currentDataset?.dataset_id,
      });
    } else {
      resetForm();
    }

    if (data.length > 0 && data[0].attribute_id) {
      setAttribute(attributes.find(x => x.attribute_id === data[0].attribute_id));
      fetchAttributeAssocFields(data[0].attribute_id);
    }
  }, [mode, data, currentDataset]);

  const fetchDatatypes = async () => {
    datatypeService
      .getAll()
      .then(res => {
        res && setDatatypes(res.datatypes);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchDatasets = async () => {
    datasetService
      .getByDatasetGroupId(dataset_group_id)
      .then(res => {
        if (res) {
          let tmp = res.datasets.map(x => {
            // For an API child dataset, render its parent endpoint as "method pill + name" in the
            // option's secondary label (formatDatasetSegment returns JSX when asPill is set).
            const parentEndpoint =
              x.datasource_type_code === 'api' && x.parent_dataset_id
                ? res.datasets.find(d => d.dataset_id === x.parent_dataset_id)
                : undefined;

            return {
              ...x,
              dataset_path: parentEndpoint
                ? formatDatasetSegment(
                    parentEndpoint.dataset_name,
                    parentEndpoint.dataset_subtype_name,
                    {
                      datasetSubtypeOperation: parentEndpoint.dataset_subtype_operation,
                      asPill: true,
                    }
                  )
                : undefined,
              // Plain-text mirror of the JSX secondary label so the parent endpoint stays filterable
              // (formatDatasetSegment returns a string when asPill/link are omitted).
              searchText: parentEndpoint
                ? formatDatasetSegment(
                    parentEndpoint.dataset_name,
                    parentEndpoint.dataset_subtype_name
                  )
                : undefined,
            };
          });

          // Exclude top-level datasets, like api endpoints
          tmp = tmp.filter(x => !(x.datasource_type_code === 'api' && !x.parent_dataset_id));

          setDatasets(tmp);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchParentFields = async dataset_id => {
    fieldService
      .getByDatasetId(dataset_id)
      .then(res => {
        if (res) {
          let result = res.fields;

          if (data.length > 0 && mode !== 'add_child') {
            // Remove self from list if not add
            result = result.filter(x => !data.find(y => y.field_id === x.field_id));
          }

          // Only display fields that can have children, like array and object
          setFields(
            result
              .filter(x => x.datatype_has_children)
              .map(x => ({
                ...x,
                field_path: getHierarchyPath(x, res.fields),
              }))
          );
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        setLoadingParentFields(false);
      });
  };

  const fetchAttributes = async () => {
    attributeService
      .getAll()
      .then(res => {
        if (res) {
          setAttributes(res.attributes);

          data[0] &&
            setAttribute(res.attributes.find(x => x.attribute_id === data[0].attribute_id));
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeAssocFields = async id => {
    if (id && id !== '') {
      fieldService
        .getByAttributeId(id)
        .then(res => {
          if (res) {
            // Remove this field from list to give correct number
            const tmp = res.fields.filter(x => {
              return data[0] && x.attribute_id !== data[0].attribute_id;
            });
            setAttributeAssocFields(tmp);
          }
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    } else {
      setAttributeAssocFields([]);
    }
  };

  /**
   * Looks up assets already using the name typed for the new attribute, in every glossary. Severity is
   * decided on render, since it depends on the glossary chosen below. Debounced: this runs per keystroke
   * and makes two requests.
   * @param {string} name
   */
  const { debounce: fetchAssetsByName } = useDebounce(name => {
    fetchAssetsWithSameName({ name })
      .then(setAssetsWithSameName)
      .catch(err => {
        showMessage(err, 'error');
      });
  }, 300);

  /**
   * Loads the glossaries the new attribute can be created in.
   */
  const fetchGlossaries = async () => {
    glossaryService
      .getAll()
      .then(res => {
        res && setGlossaries(res.glossaries);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Loads the entities of one glossary, which are the only possible parents for an attribute.
   * @param {number} glossaryId
   */
  const fetchParentEntities = async glossaryId => {
    if (!glossaryId) {
      setParentEntities([]);
      return;
    }

    entityService
      .getByGlossaryId(glossaryId)
      .then(res => {
        res && setParentEntities(res.entities);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeRoles = async () => {
    attributeRoleService
      .getAll()
      .then(res => {
        res && setAttributeRoles(res.attribute_roles);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeSensitivities = async () => {
    attributeSensitivityService
      .getAll()
      .then(res => {
        if (res) {
          const tmp = res.attribute_sensitivities.map(x => ({
            ...x,
            className: AttributeSensitivity[x.attribute_sensitivity_level].className,
          }));
          setAttributeSensitivities(tmp);

          setNewAttribute(prev => {
            return {
              ...prev,
              attribute_sensitivity_id: res.attribute_sensitivities.find(x => x.is_default)
                .attribute_sensitivity_id,
            };
          });
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchAttributeUnits = async () => {
    attributeUnitService
      .getAll()
      .then(res => {
        res && setAttributeUnits(res.attribute_units);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addFieldAssociation = async event => {
    let { value, label, icon, secondaryLabel } = '';

    value = event.value;
    label = event.option.label;
    icon = event.option.icon;
    secondaryLabel = event.option.secondaryLabel;

    const field = {
      field_id: value,
      attribute_id: formData.attribute_id,
      field_name: label,
      icon,
      secondaryLabel,
      isNew: true,
    };

    let newTmpFieldAssociations = [];
    if (tmpFieldAssociations.findIndex(x => x.field_id === value) > -1) {
      newTmpFieldAssociations = tmpFieldAssociations.filter(x => x.field_id !== value);
    } else {
      newTmpFieldAssociations = [...tmpFieldAssociations, ...[field]];
    }

    setTmpFieldAssociations(newTmpFieldAssociations);
  };

  const handleSelectAll = async () => {
    const res = await fetchAllSearchResults();

    let newTmpFieldAssociations = [];

    res.searchResults.forEach(result => {
      const isSelf = result.searchable.field_id === data[0].field_id;

      const field = {
        field_id: result.searchable.field_id,
        attribute_id: formData.attribute_id,
        field_name: result.searchable.field_name,
        icon: result.searchable.datatype_category,
        secondaryLabel: formatPath([
          result.searchable.system_name,
          result.searchable.datasource_name,
          result.searchable.dataset_group_name,
          result.searchable.dataset_name,
        ]),
      };

      if (!isSelf && tmpFieldAssociations.findIndex(x => x.field_id === field.field_id) < 0) {
        newTmpFieldAssociations.push(field);
      }
    });

    if (newTmpFieldAssociations.length > 0) {
      setTmpFieldAssociations([...tmpFieldAssociations, ...newTmpFieldAssociations]);
    } else {
      newTmpFieldAssociations = [...tmpFieldAssociations];

      res.searchResults.forEach(result => {
        newTmpFieldAssociations = newTmpFieldAssociations.filter(x => {
          return x.field_id !== result.searchable.field_id;
        });
      });
      setTmpFieldAssociations([...newTmpFieldAssociations]);
    }

    return res;
  };

  const removeFieldAssociation = item => {
    setTmpFieldAssociations(prev => prev.filter(x => x.field_id !== item.field_id));
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (name) {
      case 'attribute_id':
        setAttribute(attributes.find(x => x.attribute_id === value));
        fetchAttributeAssocFields(value);
        break;
      case 'dataset_id':
        fetchParentFields(value);
        if (value !== formData.dataset_id)
          setFormData(prev => ({ ...prev, parent_field_id: null }));
        break;
      case 'batch_attribute_id':
        if (!attribute) setAttributeAssocFields([]);
        break;
      case 'attribute_name':
        fetchAssetsByName(value);
        break;
      case 'glossary_id':
        // A parent belongs to one glossary, so a parent chosen under the previous one is no longer
        // valid. The glossary_id itself is set by the 'newAttribute' form part below.
        setNewAttribute(prev => ({ ...prev, parent_entity_id: null }));
        fetchParentEntities(value);
        break;
      case 'fd_status_id':
        setNewAttribute(prev => ({
          ...prev,
          status_id: value,
        }));
        break;
      case 'add_attribute':
        const attributeName = newAttribute.attribute_name
          ? newAttribute.attribute_name
          : formatUserFriendly(formData.field_name);
        const attributeDescription =
          editorStateToPlainText(newAttribute.attribute_description) !== ''
            ? newAttribute.attribute_description
            : (data[0]?.field_source_description_secondary &&
                formData?.field_source_description_secondary) ||
              (data[0]?.field_source_description && formData?.field_source_description) ||
              newAttribute.attribute_description;

        // An attribute has to live in a glossary. Default to the glossary of the attribute the field
        // is currently associated with, then to the only glossary there is; otherwise leave it unset
        // so the user has to choose, since the choice decides where the asset can be found and which
        // names it has to be unique among.
        const defaultGlossaryId =
          newAttribute.glossary_id ||
          attribute?.glossary_id ||
          (glossaries.length === 1 ? glossaries[0].glossary_id : null);

        setNewAttribute({
          ...newAttribute,
          attribute_name: attributeName,
          attribute_description: attributeDescription,
          glossary_id: defaultGlossaryId,
          status_id: newAttribute.status_id || statuses.default_glossary_status_id,
          owner_principal_id: newAttribute.owner_principal_id || loggedInUser.principal_id,
          attribute_action: value ? 'insert' : 'update',
          attribute_role_id: attributeRoles?.filter(
            x => x.attribute_role_name.toLowerCase() === AttributeRole.dimension.toLowerCase()
          )[0].attribute_role_id,
        });

        setAddAttribute(value);
        fetchAssetsByName(attributeName);
        fetchParentEntities(defaultGlossaryId);
        break;
      case 'is_pii':
        // Check currently selected sensitivity class and change to lowest allowed sensitivity class if current class is not allowed
        if (
          !attributeSensitivities.find(
            x => x.attribute_sensitivity_id === newAttribute.attribute_sensitivity_id
          ).is_allowed_for_pii
        ) {
          setNewAttribute(prev => {
            return {
              ...prev,
              attribute_sensitivity_id: attributeSensitivities
                .filter(x => x.is_allowed_for_pii)
                .reduce(function (prev, curr) {
                  return prev.attribute_sensitivity_level > curr.attribute_sensitivity_level
                    ? prev
                    : curr;
                }).attribute_sensitivity_id,
            };
          });
        }
        break;
      default:
        break;
    }

    switch (formPart) {
      case 'newAttribute':
        setNewAttribute(prev => {
          return { ...prev, [name]: value };
        });
        break;
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      case 'addAttribute':
        // Do nothing, otherwise the "add_attribute" field will be added to formData
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const handleClickOnDescription = () => {
    if (formData.field_description_source !== 'k') {
      setFormData(prevFormData => {
        return {
          ...prevFormData,
          field_description_source: 'k',
        };
      });
    }
  };

  const handleSearchFieldInputChange = value => {
    setSearchTerm(value);
  };

  const handleSearchAttributeInputChange = value => {
    fd.setSearchTerm(value);
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        const tmp_field = {
          field_id: item.field_id,
          field_name: item.field_name,
          field_technical_definition: item.field_technical_definition,
          dataset_id: item.dataset_id,
          parent_field_id: data[0].parent_field_id,
          datatype_id: item.datatype_id,
          datatype_length: item.datatype_length,
          datatype_precision: item.datatype_precision,
          datatype_scale: item.datatype_scale,
          is_nullable: item.is_nullable,
          default_value: item.default_value,
          is_primary_key: item.is_primary_key,
          attribute_id: item.field_description_source === 'k' ? item.attribute_id : null,
          field_description_source: item.field_description_source,
          status_id: item.status_id,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.field_id),
        };

        return tmp_field;
      });
    } else {
      const tmpFormData = {
        ...formData,
        attribute_id: formData.field_description_source === 'k' ? formData.attribute_id : null,
        // The editor holds Draft.js state; the API takes the text. Empty comes back as '', which is
        // what this field submitted before it was a code editor.
        field_technical_definition: editorStateToPlainText(formData.field_technical_definition),
      };

      if (addAttribute) {
        let field_associations = tmpFieldAssociations.map(field => ({
          field_id: field.field_id,
        }));

        dataToSubmit = {
          ...tmpFormData,
          ...getCustomPropertyValuesForSubmit(),
          attribute: {
            ...newAttribute,
            attribute_description: textareaToRaw({
              value: newAttribute.attribute_description,
            }),
            attribute_definition: textareaToRaw({
              value: newAttribute.attribute_definition,
            }),
            // Only a Measure is quantified, so a Dimension is saved without a unit whatever the
            // form last held. The API clears it as well, for a client that does not go through it.
            attribute_unit_id: newAttributeIsMeasure ? newAttribute.attribute_unit_id : null,
            field_associations,
          },
        };
      } else {
        dataToSubmit = {
          ...tmpFormData,
          ...getCustomPropertyValuesForSubmit(),
        };
      }

      delete dataToSubmit.field_source_description;
      delete dataToSubmit.field_source_description_secondary;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData(() => {
      let tmp = {
        ...initialFormState,
        dataset_id: data[0]?.dataset_id || currentDataset?.dataset_id || null,
        status_id: statuses.default_system_status_id,
      };

      if (mode === 'add_child') {
        tmp = {
          ...tmp,
          parent_field_id: data[0].field_id,
        };
      }

      return tmp;
    });
    setNewAttribute(() => {
      return { ...initialAttribute };
    });
    setAddAttribute(false);

    fetchParentFields(data[0]?.dataset_id || currentDataset?.dataset_id);
  };

  const attributeAssocFieldsTooltip = () => {
    return attributeAssocFields
      .filter(x => x.field_id !== formData.attribute_id)
      .map((field, index) => (
        <React.Fragment key={index}>
          <div className="tooltip-title thin">
            <Icon name={field.datatype_category} />
            {field.field_name}
          </div>
          <div className="tooltip-subtitle inverted-margin">
            {formatPath([
              field.system_name,
              field.datasource_name,
              field.dataset_group_name,
              field.dataset_name,
              field.field_name,
            ])}
          </div>
        </React.Fragment>
      ));
  };

  /**
   * Lists the assets already using this name, each with the glossary it belongs to.
   * @param {object[]} assets - Normalised matches from getSameNameValidation.
   */
  const sameNameTooltip = assets => {
    return assets.map((asset, index) => (
      <React.Fragment key={index}>
        <div className="tooltip-title">
          <Icon name={asset.icon} />
          {asset.name}
          {asset.glossaryName && <span className="grey-text"> — {asset.glossaryName}</span>}
        </div>
        <div className="tooltip-body inverted-margin">{textareaToPlainText(asset.description)}</div>
      </React.Fragment>
    ));
  };

  /**
   * The duplicate-name message for the attribute being created here. An attribute created from this
   * form is always an Attribute, so the clash that matters is another attribute in the glossary chosen
   * below; anything else is allowed and reported as information.
   * @returns {{valid: boolean|string, message: string, tooltipBody?: object}|undefined}
   */
  const getNewAttributeNameValidation = () => {
    const validation = getSameNameValidation({
      matches: assetsWithSameName,
      glossaryId: newAttribute.glossary_id,
      targetObjectName: Asset.Attribute,
    });

    return validation && { ...validation, tooltipBody: sameNameTooltip(validation.assets) };
  };

  const getSensitivityOptions = () => {
    return newAttribute.is_pii
      ? formatSelectOptions({
          options: attributeSensitivities.map(x => {
            return { ...x, is_not_allowed_for_pii: !x.is_allowed_for_pii };
          }),
          optionValue: 'attribute_sensitivity_id',
          optionLabel: 'attribute_sensitivity_name',
          optionDisabled: 'is_not_allowed_for_pii',
          tooltip: 'attribute_sensitivity_description',
          className: 'className',
        })
      : formatSelectOptions({
          options: attributeSensitivities,
          optionValue: 'attribute_sensitivity_id',
          optionLabel: 'attribute_sensitivity_name',
          tooltip: 'attribute_sensitivity_description',
          className: 'className',
        });
  };

  const renderAttributeSensitivityControl = () => {
    const name = 'attribute_sensitivity_id';
    const label = 'Sensitivity Class';
    const value = newAttribute.attribute_sensitivity_id;
    let options = getSensitivityOptions();
    const title = `Set the sensitivity class of this attribute.\nThis does not have any functional effect in Katalogue, it is merely a property of attributes.\nSee your organization's definition of sensitivity classification.`;
    const onChange = e => handleInputChange(e, 'newAttribute');
    const disabled = loading;

    if (options.length > 4) {
      return (
        <Select
          name={name}
          label={label}
          value={value}
          options={options}
          title={title}
          onChange={onChange}
          disabled={disabled}
          sortOptions={false}
        />
      );
    } else {
      return (
        <SelectButtons
          name={name}
          label={label}
          value={value}
          options={options}
          title={title}
          onChange={onChange}
          disabled={disabled}
        />
      );
    }
  };

  const currentDatatype =
    formData.datatype_id && datatypes.find(x => x.datatype_id === formData.datatype_id);
  const hasLength = currentDatatype?.has_length;
  const hasPrecision = currentDatatype?.has_precision;
  const hasScale = currentDatatype?.has_scale;
  const hasDatatypeExtendedProperty = hasLength || hasPrecision || hasScale;

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="field_name"
            value={formData.field_name}
            statusValue={formData.status_id}
            assetCategory="system"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.field_name ? { valid: false, message: formErrors.field_name } : undefined
            }
          />
        </div>

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_dataset_id}
                    name="batch_dataset_id"
                    label="Edit Dataset"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="dataset_id"
                label={!batchEdit}
                value={formData.dataset_id === 'multiple' ? undefined : formData.dataset_id}
                options={formatSelectOptions({
                  options: datasets,
                  optionValue: 'dataset_id',
                  optionLabel: 'dataset_name',
                  optionIcon: 'dataset_type_name',
                  optionSecondaryLabel: 'dataset_path',
                })}
                onChange={handleInputChange}
                placeholder={formData.dataset_id === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_dataset_id)}
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_parent_field_id}
                    name="batch_parent_field_id"
                    label="Edit Parent Field"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="parent_field_id"
                label={!batchEdit}
                title={`Assign this field as a child to another Field in this dataset.\nNote that only fields with a datatype that supports nesting, like arrays and objects, can be assigned as parent`}
                value={
                  formData.parent_field_id === 'multiple' ? undefined : formData.parent_field_id
                }
                options={formatSelectOptions({
                  options: fields,
                  optionIcon: 'datatype_category',
                  optionValue: 'field_id',
                  optionLabel: 'field_name',
                  optionSecondaryLabel: 'field_path',
                  tooltip: 'field_description',
                })}
                onChange={handleInputChange}
                placeholder={
                  formData.parent_field_id === 'multiple'
                    ? '< Multiple >'
                    : fields.length > 0 || loadingParentFields
                      ? 'Select parent field...'
                      : 'No fields with datatypes that support nesting found'
                }
                disabled={
                  loading ||
                  (batchEdit && !batchEditItems.batch_parent_field_id) ||
                  (fields.length < 1 && !loadingParentFields)
                }
                isNullable={true}
                isOptional={true}
              ></Select>
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_datatype_id}
                    name="batch_datatype_id"
                    label="Edit Datatype"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="datatype_id"
                label={!batchEdit}
                value={formData.datatype_id === 'multiple' ? undefined : formData.datatype_id}
                options={formatSelectOptions({
                  options: datatypes,
                  optionValue: 'datatype_id',
                  optionLabel: 'datatype_name',
                  optionIcon: 'datatype_category',
                })}
                onChange={handleInputChange}
                placeholder={
                  formData.datatype_id === 'multiple' ? '< Multiple >' : 'Select datatype...'
                }
                disabled={loading || (batchEdit && !batchEditItems.batch_datatype_id)}
                validation={
                  formErrors.datatype_id
                    ? { valid: false, message: formErrors.datatype_id }
                    : undefined
                }
              />
            </div>

            {hasDatatypeExtendedProperty && (
              <div className="form-block vertical">
                {hasLength && (
                  <React.Fragment>
                    {batchEdit && (
                      <label>
                        <Checkbox
                          checked={batchEditItems.batch_datatype_length}
                          name="batch_datatype_length"
                          label="Edit Length"
                          onChange={e => handleInputChange(e, 'batch')}
                          disabled={loading}
                        />
                      </label>
                    )}
                    <Input
                      type="text"
                      name="datatype_length"
                      labelPosition="left"
                      value={
                        formData.datatype_length === 'multiple'
                          ? undefined
                          : formData.datatype_length
                      }
                      onChange={handleInputChange}
                      title="Datatype length"
                      placeholder={formData.datatype_length === 'multiple' ? '< Multiple >' : ''}
                      disabled={loading || (batchEdit && !batchEditItems.batch_datatype_length)}
                      validation={
                        formErrors.datatype_length
                          ? { valid: false, message: formErrors.datatype_length }
                          : undefined
                      }
                      isOptional={true}
                    />
                  </React.Fragment>
                )}

                {hasPrecision && (
                  <React.Fragment>
                    {batchEdit && (
                      <label>
                        <Checkbox
                          checked={batchEditItems.batch_datatype_precision}
                          name="batch_datatype_precision"
                          label="Edit Precision"
                          onChange={e => handleInputChange(e, 'batch')}
                          disabled={loading}
                        />
                      </label>
                    )}
                    <Input
                      type="text"
                      name="datatype_precision"
                      labelPosition="left"
                      value={
                        formData.datatype_precision === 'multiple'
                          ? undefined
                          : formData.datatype_precision
                      }
                      onChange={handleInputChange}
                      title="Datatype precision. Must be a number"
                      placeholder={formData.datatype_precision === 'multiple' ? '< Multiple >' : ''}
                      disabled={loading || (batchEdit && !batchEditItems.batch_datatype_precision)}
                      validation={
                        formErrors.datatype_precision
                          ? { valid: false, message: formErrors.datatype_precision }
                          : undefined
                      }
                      isOptional={true}
                    />
                  </React.Fragment>
                )}

                {hasScale && (
                  <React.Fragment>
                    {batchEdit && (
                      <label>
                        <Checkbox
                          checked={batchEditItems.batch_datatype_scale}
                          name="batch_datatype_scale"
                          label="Edit Scale"
                          onChange={e => handleInputChange(e, 'batch')}
                          disabled={loading}
                        />
                      </label>
                    )}
                    <Input
                      type="text"
                      name="datatype_scale"
                      labelPosition="left"
                      value={
                        formData.datatype_scale === 'multiple' ? undefined : formData.datatype_scale
                      }
                      onChange={handleInputChange}
                      title="Datatype scale. Must be a number"
                      placeholder={formData.datatype_scale === 'multiple' ? '< Multiple >' : ''}
                      disabled={loading || (batchEdit && !batchEditItems.batch_datatype_scale)}
                      validation={
                        formErrors.datatype_scale
                          ? { valid: false, message: formErrors.datatype_scale }
                          : undefined
                      }
                      isOptional={true}
                    />
                  </React.Fragment>
                )}
              </div>
            )}

            {!batchEdit && (
              <div className="form-block vertical">
                <Checkbox
                  name="is_primary_key"
                  checked={formData.is_primary_key}
                  onChange={handleInputChange}
                  label="Is Primary Key"
                  disabled={loading}
                />
              </div>
            )}

            {batchEdit && (
              <label>
                <Checkbox
                  checked={batchEditItems.batch_is_nullable}
                  name="batch_is_nullable"
                  label="Edit Is Nullable"
                  onChange={e => handleInputChange(e, 'batch')}
                  disabled={loading}
                />
              </label>
            )}
            <div className="form-block vertical">
              <Checkbox
                name="is_nullable"
                checked={formData.is_nullable}
                onChange={handleInputChange}
                title="Nullability of the field. Check this option if this field allows for NULL values"
                label="Is Nullable"
                disabled={loading || (batchEdit && !batchEditItems.batch_is_nullable)}
              />
            </div>

            {batchEdit && (
              <label>
                <Checkbox
                  checked={batchEditItems.batch_default_value}
                  name="batch_default_value"
                  label="Edit Default Value"
                  onChange={e => handleInputChange(e, 'batch')}
                  disabled={loading}
                />
              </label>
            )}
            <div className="form-block vertical">
              <TextareaAutosize
                name="default_value"
                label={!batchEdit ? 'Default Value' : undefined}
                value={formData.default_value === 'multiple' ? '' : formData.default_value || ''}
                onChange={handleInputChange}
                minRows={1}
                disabled={loading || (batchEdit && !batchEditItems.batch_default_value)}
                placeholder={formData.default_value === 'multiple' ? '< Multiple >' : 'NULL'}
                className="code"
                isOptional={true}
              />
            </div>

            {!batchEdit && (
              <div className="form-block vertical">
                <TextareaCode
                  name="field_technical_definition"
                  label="Technical Definition"
                  value={formData.field_technical_definition}
                  onChange={handleInputChange}
                  disabled={loading}
                  // The syntax follows the dataset the field belongs to, so a Power BI measure is
                  // highlighted as DAX and a database column as SQL. Reads the dataset selected in
                  // the form rather than the field's stored one, so changing it updates the editor.
                  language={technicalDefinitionLanguage(
                    datasets.find(x => x.dataset_id === formData.dataset_id)?.datasource_type_code,
                    Asset.Field
                  )}
                  showLineNumbers={false}
                  expandable={true}
                  isOptional={true}
                />
              </div>
            )}
          </div>

          <div label="description">
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_field_description_source}
                    name="batch_field_description_source"
                    label="Edit Description Source"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectButtons
                label={!batchEdit}
                name="field_description_source"
                value={
                  formData.field_description_source === 'multiple'
                    ? undefined
                    : formData.field_description_source
                }
                options={getDescriptionSourceOptions()}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_field_description_source)}
                title="Select whether to use a custom description set here in Katalogue or automatically sync the description from the datasource."
              />
            </div>

            {formData.field_description_source === 'k' && (
              <React.Fragment>
                {!batchEdit && (
                  <div className="form-block vertical">
                    <SelectButtons
                      label="Attribute"
                      name="add_attribute"
                      value={addAttribute}
                      options={formatSelectOptions({
                        options: [
                          {
                            value: false,
                            label: 'Use Existing',
                            tooltip: 'Associate with an existing Attribute',
                          },
                          {
                            value: true,
                            label: 'Add New',
                            tooltip: 'Add a new Attribute',
                          },
                        ],
                        optionValue: 'value',
                        optionLabel: 'label',
                        tooltip: 'tooltip',
                      })}
                      onChange={e => handleInputChange(e, 'addAttribute')}
                      disabled={loading}
                    />
                  </div>
                )}

                {!addAttribute && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      {batchEdit && (
                        <label>
                          <Checkbox
                            checked={batchEditItems.batch_attribute_id}
                            name="batch_attribute_id"
                            label="Edit Attribute"
                            onChange={e => handleInputChange(e, 'batch')}
                            disabled={loading}
                          />
                        </label>
                      )}

                      <Select
                        name="attribute_id"
                        value={
                          formData.attribute_id === 'multiple' ? undefined : formData.attribute_id
                        }
                        options={
                          fd.searchTerm
                            ? fDRequestResults
                            : formatSelectOptions({
                                options: attribute?.attribute_id ? [attribute] : [],
                                optionValue: 'attribute_id',
                                optionLabel: 'attribute_name',
                                optionIcon: 'attribute_role_name',
                                tooltip: 'attribute_description',
                              })
                        }
                        onInputChange={handleSearchAttributeInputChange}
                        loadMoreOptions={fd.loadNextPage}
                        loading={fd.loadingMoreOptions}
                        searchTerm={fd.searchTerm}
                        clearSearchResults={fd.clearSearchResults}
                        placeholder={
                          formData.attribute_id === 'multiple'
                            ? '< Multiple >'
                            : 'Search attribute...'
                        }
                        isClearable={true}
                        isNullable={true}
                        onChange={handleInputChange}
                        disabled={loading || (batchEdit && !batchEditItems.batch_attribute_id)}
                      />
                      {attributeAssocFields.length > 0 && (
                        <div
                          className="form-light-text"
                          onMouseEnter={() => {
                            showTooltip({
                              tooltipBody: attributeAssocFieldsTooltip(),
                            });
                          }}
                          onMouseLeave={hideTooltip}
                        >
                          {attributeAssocFields.length} other Fields also use this Attribute
                        </div>
                      )}
                    </div>

                    {/* != null, so an absent attribute reads as no attribute: the section below
                        renders `attribute`, which until one is chosen holds editor states rather
                        than the text they were built from. */}
                    {formData.attribute_id != null && attribute && !batchEdit && (
                      <React.Fragment>
                        <div className="form-block vertical">
                          <TextareaReadOnly
                            value={textareaInitialState({
                              value: attribute.attribute_description || '-',
                              readOnly: true,
                            })}
                            name="attribute_description"
                          />
                        </div>
                        <div className="form-block vertical">
                          <TextareaReadOnly
                            value={textareaInitialState({
                              value: attribute.attribute_definition || '-',
                              readOnly: true,
                            })}
                            name="attribute_definition"
                          />
                        </div>
                        <div className="form-block vertical disabled">
                          <Label label="Data Sensitivity" disabled={true} />
                          <div className="horizontal">
                            {attribute.is_pii && (
                              <Icon
                                name="pii"
                                tooltip="Classified as Personally Identifiable Information (PII)"
                              />
                            )}
                            <Icon
                              name="attribute_sensitivity"
                              label={attribute.attribute_sensitivity_name}
                              value={attribute.attribute_sensitivity_level}
                              values={AttributeSensitivity}
                            />
                          </div>
                        </div>

                        <div className="form-block vertical disabled">
                          <Label label="Status" disabled={true} />
                          <div className="horizontal">
                            <Pill label={attribute.status_id} values={statuses} />
                          </div>
                        </div>

                        <div className="form-block vertical disabled">
                          <Label label="Owner" disabled={true} />
                          <PrincipalLink principalId={attribute.owner_principal_id} />
                        </div>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
                {addAttribute && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <InputStatus
                        name="attribute_name"
                        label="Attribute Name"
                        value={newAttribute.attribute_name}
                        statusValue={newAttribute.status_id}
                        assetCategory="glossary"
                        onChange={e => handleInputChange(e, 'newAttribute')}
                        loading={loading}
                        validation={getNewAttributeNameValidation()}
                      />
                    </div>

                    {/* An attribute belongs to a glossary, and may sit under one of its entities.
                        Both are asked for here because the choice decides where the attribute can be
                        found and which names it has to be unique among. */}
                    <div className="form-block vertical">
                      <Select
                        name="glossary_id"
                        label="Glossary"
                        value={newAttribute.glossary_id}
                        options={formatSelectOptions({
                          options: glossaries,
                          optionValue: 'glossary_id',
                          optionLabel: 'glossary_name',
                          tooltip: 'glossary_description',
                        })}
                        placeholder="Select Glossary ..."
                        onChange={e => handleInputChange(e, 'newAttribute')}
                        disabled={loading}
                        isNullable={false}
                      ></Select>
                    </div>

                    <div className="form-block vertical">
                      <Select
                        name="parent_entity_id"
                        label="Parent Entity"
                        value={newAttribute.parent_entity_id}
                        options={formatSelectOptions({
                          options: parentEntities,
                          optionValue: 'entity_id',
                          optionLabel: 'entity_name',
                          tooltip: 'entity_description',
                        })}
                        placeholder={
                          newAttribute.glossary_id
                            ? 'Select Parent Entity ...'
                            : 'Select a Glossary first ...'
                        }
                        onChange={e => handleInputChange(e, 'newAttribute')}
                        disabled={loading || !newAttribute.glossary_id}
                        isClearable={true}
                        isNullable={true}
                      ></Select>
                    </div>

                    {/* No formErrors: these describe the new attribute this form can create on
                        the side, not the field being saved, so a rejected field of the field is
                        never one of these. */}
                    <FormTabs>
                      <div label="description">
                        <div className="form-block vertical">
                          <Textarea
                            name="attribute_description"
                            label="Description"
                            value={newAttribute.attribute_description}
                            onChange={e => handleInputChange(e, 'newAttribute')}
                            disabled={loading}
                          />
                        </div>

                        <div className="form-block vertical">
                          <Textarea
                            name="attribute_definition"
                            label="Definition"
                            value={newAttribute.attribute_definition}
                            onChange={e => handleInputChange(e, 'newAttribute')}
                            disabled={loading}
                          />
                        </div>

                        <div className="form-block vertical">
                          <SelectButtons
                            name="attribute_role_id"
                            label="Attribute Role"
                            value={newAttribute.attribute_role_id}
                            options={formatSelectOptions({
                              options: attributeRoles,
                              optionValue: 'attribute_role_id',
                              optionLabel: 'attribute_role_name',
                              optionIcon: 'attribute_role_name',
                            })}
                            onChange={e => handleInputChange(e, 'newAttribute')}
                            disabled={loading}
                          />
                        </div>

                        {/* A unit measures the values an attribute describes, so it belongs to a
                            Measure. A Dimension is enumerated and carries Attribute Values
                            instead. */}
                        {newAttributeIsMeasure && (
                          <div className="form-block vertical">
                            <Select
                              name="attribute_unit_id"
                              label="Attribute Unit"
                              value={newAttribute.attribute_unit_id}
                              options={formatSelectOptions({
                                options: attributeUnits,
                                optionValue: 'attribute_unit_id',
                                optionLabel: 'attribute_unit_code',
                                optionSecondaryLabel: 'attribute_unit_name',
                              })}
                              isNullable={true}
                              onChange={e => handleInputChange(e, 'newAttribute')}
                              placeholder="Select attribute unit..."
                              disabled={loading}
                            />
                          </div>
                        )}

                        <div className="form-block vertical">
                          {
                            <SelectOwner
                              name="owner_principal_id"
                              value={newAttribute.owner_principal_id}
                              label="Owner"
                              onChange={e => handleInputChange(e, 'newAttribute')}
                              disabled={loading}
                            />
                          }
                        </div>
                      </div>

                      <div label="Sensitivity">
                        <div className="form-block vertical">
                          {renderAttributeSensitivityControl()}
                        </div>

                        <div className="form-block horizontal">
                          <Checkbox
                            name="is_pii"
                            checked={newAttribute.is_pii}
                            onChange={e => handleInputChange(e, 'newAttribute')}
                            title={`Tag this attribute as PII.\nThis tag will be propagated to all fields associated with this attribute, and aggregated to the dataset level.\nThis does not have any other functional effect in Katalogue, it is merely a property of attributes.\nSee your organization's definition of what should be considered PII or not.`}
                            label="Personally Identifiable Information (PII)"
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div label="Fields" resultCount={tmpFieldAssociations.length}>
                        <div className="form-block vertical">
                          <Select
                            name="field_id"
                            value={tmpFieldAssociations.map(x => {
                              return { value: x.field_id, label: x.field_name };
                            })}
                            label={false}
                            options={requestResults}
                            onInputChange={handleSearchFieldInputChange}
                            loadMoreOptions={loadNextPage}
                            loading={loadingMoreOptions}
                            resultCount={searchResultsCount && searchResultsCount.field}
                            searchTerm={searchTerm}
                            showSearchResultCount={true}
                            onSelectAll={handleSelectAll}
                            clearSearchResults={clearSearchResults}
                            placeholder="Search Fields to add..."
                            isMulti={true}
                            isClearable={true}
                            onChange={addFieldAssociation}
                            disabled={loading}
                            showMultiSelectedElements={false}
                          />
                        </div>

                        <div className="form-block vertical">
                          <label className="form-strong-text">
                            {tmpFieldAssociations.length} Other Associated Fields
                          </label>
                          {tmpFieldAssociations.map((item, index) => {
                            return (
                              <div
                                className={
                                  'side-dialog-search-result' + (loading ? ' disabled' : '')
                                }
                                key={index}
                                onClick={() => {
                                  !loading && removeFieldAssociation(item);
                                }}
                              >
                                <div className="side-dialog-search-result-icon">
                                  <Icon name={item.icon} />
                                </div>
                                <div className="side-dialog-search-result-body">
                                  <div className="side-dialog-search-result-title">
                                    {item.field_name}
                                  </div>
                                  <div className="side-dialog-search-result-subtitle">
                                    {item.secondaryLabel}
                                  </div>
                                </div>
                                {item.isNew && (
                                  <div className="side-dialog-search-result-is-new"></div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </FormTabs>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            {formData.field_description_source !== 'k' && !batchEdit && (
              <div className="form-block vertical">
                <Textarea
                  name="field_description"
                  label="Description"
                  value={getDescription()}
                  onClick={handleClickOnDescription}
                  disabled={true}
                  title="The description is automatically synced from the datasource"
                />
              </div>
            )}
          </div>

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddFieldForm = forwardRef(_AddFieldForm);
